import {
	notificationError,
	checkForColumnFilter,
	TableFilterUtility,
	RemoveFiltersModalUtility,
	checkForColumnWidth,
	isSuperAdminOrganization,
	isWarehouse,
	translate,
	validateNumberField,
	validateMinValue,
	validateDecimalLengthField,
	getDecimalPounds,
	isSeller,
} from 'core/utils';
import {
	BaseListViewStore,
	BooleanCellData,
	BooleanToggleCellData,
	DescriptionCellData,
	ProductHyperLinkCellData,
	WeightCellData,
	MultipleCellData,
} from 'core/stores';
import { observable, action, makeObservable } from 'mobx';
import { localStorageHandler } from 'core/services';
import { BaasicDropdownViewStore, BaasicTableSettingsViewStore } from 'common/stores';
import { ApplicationService, ProductUpcService } from 'common/data';
import { CarrierStore } from 'modules/carrier/stores';
import { ChannelStore } from 'modules/channel/stores';
import { ProductStore } from 'modules/product/stores';
import { LookupStore } from 'modules/lookup/stores';
import { TableFilterTemplate, TableFilterNumericTemplate } from 'themes/components';
import { conversionRates, lookupRouteName, sourceOfChange } from 'common/constants';
import { ProductImportForm } from 'modules/product/forms';
import { saveAs } from 'file-saver';
import XLSX from 'xlsx';
import _ from 'lodash';
import * as excelJS from 'exceljs';
import { ProductImport } from '../pages';
import { ApplicationStore } from 'platform/application/stores';

class ProductListViewStore extends BaseListViewStore {
	@observable change = false;
	@observable showOnlyActiveSkus = false;
	@observable isVirtualSkus = false;
	displaySellerDropdown = false;
	conditionOptions = [];
	productStatusOptions = [];
	productCategoryOptions = [];
	weightUnitOptions = [];
	dimensionUnitOptions = [];
	boxOptions = [];
	localStorageWidthKey = 'products-column-width';
	productImportForm = new ProductImportForm();

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ProductStore(rootStore),
			queryParams: {
				embed: [
					'dimensionUnit',
					'weightUnit',
					'productStatus',
					'productCategory',
					'seller',
					'condition',
					'box',
					'productUpcs',
					'productInventoryRestockDate',
					// Have to use this embed because toggling isAvailable for the product deletes all the SKUs otherwise
					'productVirtualSkus',
					'productVirtualSkus.channel',
					'productVirtualSkus.channel.channelType',
					'productVirtualSkus.channel.productInventoryRestockDate',
					'productVirtualSkus.productVirtualSkuCarriers',
				],
				filter: localStorageHandler.get('product-filter'),
			},
			findFunction: 'findProduct',
			tableData: {
				columns: [
					{
						key: 'mainSku',
						title: 'PRODUCTVIEW.PRODUCTSKU',
						dataCell: ProductHyperLinkCellData,
						onClick: item => this.routes.detail(item.id),
						selected: true,
						width: 200,
						filter: null,
						locked: true,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
					},
					{
						key: 'seller.applicationTitle',
						title: 'PRODUCTVIEW.SELLER',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						dontSplit: true,
						filterApiParams: {
							sellerOnly: true,
						},
						enabled: isWarehouse(rootStore) || isSuperAdminOrganization(rootStore),
						service: 'applicationService',
					},
					{
						key: 'name',
						title: 'PRODUCTVIEW.NAME',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'width',
						title: 'PRODUCTVIEW.WIDTH',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'length',
						title: 'PRODUCTVIEW.LENGTH',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'height',
						title: 'PRODUCTVIEW.HEIGHT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
					{
						key: 'dimensionUnit.name',
						title: 'PRODUCTVIEW.DIMENSIONUNIT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.dimensionUnit,
					},
					{
						key: 'weight',
						title: 'PRODUCTVIEW.WEIGHT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
						dataCell: WeightCellData,
					},
					{
						key: 'weightUnit.name',
						title: 'PRODUCTVIEW.WEIGHTUNIT',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.weightUnit,
					},
					{
						key: 'onePerBox',
						title: 'PRODUCTVIEW.ONEPERBOX',
						dataCell: BooleanCellData,
						selected: false,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'description',
						title: 'PRODUCTVIEW.DESCRIPTION',
						maxChars: 20,
						dataCell: DescriptionCellData,
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'asin',
						title: 'PRODUCTVIEW.ASIN',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'productUpcs.upc',
						title: 'PRODUCTVIEW.UPC',
						selected: false,
						dataCell: MultipleCellData,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						service: 'ProductUpcService',
						dontSplit: true,
					},
					{
						key: 'productStatus.name',
						title: 'PRODUCTVIEW.PRODUCTSTATUS',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.productStatus,
					},
					{
						key: 'productCategory.name',
						title: 'PRODUCTVIEW.PRODUCTCATEGORY',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.productCategory,
					},
					{
						key: 'condition.name',
						title: 'PRODUCTVIEW.CONDITION',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
						lookupConstant: lookupRouteName.condition,
					},
					{
						key: 'isActive',
						title: 'PRODUCTVIEW.ISACTIVE',
						dataCell: BooleanToggleCellData,
						onClick: product =>
							!product.isActive && product.isShippable
								? this.openConfirmationModal(this.getProductActivityConfirmModalProps(product))
								: this.isActiveToggle(product),
						width: 200,
						sortable: true,
						filter: null,
						selected: false,
					},
					{
						key: 'isAutomatedTransferEnabled',
						title: 'PRODUCTVIEW.AUTOMATICTRANSFER',
						dataCell: BooleanToggleCellData,
						onClick: product => this.toggleAutomatedTransfer(product),
						width: 200,
						enabled: isSeller(rootStore) || isSuperAdminOrganization(rootStore),
						sortable: true,
						filter: null,
						selected: false,
					},
					{
						key: 'isMultiProduct',
						title: 'PRODUCTVIEW.MULTIPACKAGED',
						dataCell: BooleanCellData,
						sortable: true,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
					},
					{
						key: 'isShippable',
						title: 'PRODUCTVIEW.ISSHIPPABLE',
						dataCell: BooleanCellData,
						sortable: true,
						width: 120,
						filter: null,
						filterType: TableFilterTemplate,
						selected: false,
					},
					{
						key: 'nmfcCode',
						title: 'PRODUCTVIEW.NMFCCODE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterTemplate,
						sortable: true,
					},
					{
						key: 'freightClassCode',
						title: 'PRODUCTVIEW.FREIGHTCLASSCODE',
						selected: false,
						width: 200,
						filter: null,
						filterType: TableFilterNumericTemplate,
						sortable: true,
					},
				],
				sortObject: [
					{
						field: 'mainSku',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onDelete: item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
					onLinkProduct: item => this.openConfirmationModal(this.getLinkProductConfirmModalProps(item)),
				},
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('productSection.update'),
				onDelete: () => this.authStore.hasPermission('productSection.remove'),
				onLinkProduct: () => this.authStore.hasPermission('productSection.full'),
			},
			routes: {
				detail: id =>
					this.rootStore.routerStore.goTo('master.app.main.product.list.detail', {
						id,
					}),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.product.list.edit', {
						id,
					}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.product.list.add', {}),
				list: () => this.rootStore.routerStore.goTo('master.app.main.product.list'),
				productActivity: id =>
					this.rootStore.routerStore.goTo('master.app.main.product.list.activity', {
						id,
					}),
				import: () => this.rootStore.routerStore.setQueryParams({ importData: true }),
			},
		});
		makeObservable(this);
		if (isWarehouse(rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		if (isSuperAdminOrganization(rootStore)) {
			this.productImportForm.$('sellerId').set('rules', 'required');
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
			this.displaySellerDropdown = true;
		}
		this.queryUtility.initialize(this.findProduct);
		checkForColumnFilter(localStorageHandler.get('product-table-data'), this.tableData.columns);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.columnFilterSetup = 'product-table-data';
		this.lookupStore = new LookupStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.removeFiltersModal = new RemoveFiltersModalUtility({ viewStore: this });
		this.tableFilter = new TableFilterUtility({
			lookupStore: this.lookupStore,
			listStore: this.listStore,
			queryUtility: this.queryUtility,
			columns: this.tableData.columns,
			services: [
				this.listStore.service,
				new ApplicationService(this.rootStore.app.baasic.apiClient),
				new ProductUpcService(this.rootStore.app.baasic.apiClient),
			],
		});
		this.setButtons();
		this.setHeaderModes();
		this.setSearchVisible();
	}

	openImportProductsModal = isProductVirtualSkuImport => {
		this.isVirtualSkus = isProductVirtualSkuImport;
		this.openModal({
			onClose: this.closeProductImportModal,
			onOpen: this.initializeSellerDropdown,
			component: ProductImport,
			className: 'ReactModal__Content ReactModal--med',
		});
	};

	@action.bound
	initializeSellerDropdown = async () => {
		this.sellerDropdownStore = new BaasicDropdownViewStore({
			field: this.productImportForm.$('sellerId'),
			store: new ApplicationStore(this.rootStore),
			filterProperties: {
				sellerOnly: true,
			},
			itemTemplate: {
				value: 'id',
				label: 'applicationTitle',
			},
		});
		const seller = localStorageHandler.get('selectedSeller');
		if (seller) {
			this.sellerDropdownStore.setValue(seller);
		}
	};

	@action.bound
	isActiveToggle = async product => {
		this.suspend();
		try {
			if (!isWarehouse(this.rootStore)) {
				await this.listStore.setProductActivity(product, sourceOfChange.editProductIsActiveToggle);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.PRODUCT', { name: product.name }]);
				product.isActive = !product.isActive;
			} else {
				this.rootStore.notificationStore.error(['PRODUCTVIEW.PERMISSION']);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	toggleAutomatedTransfer = async product => {
		this.suspend();
		try {
			product.isAutomatedTransferEnabled = !product.isAutomatedTransferEnabled;
			await this.listStore.updateProduct(product, sourceOfChange.editProductEnableAutomatedTransfer);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.PRODUCT', { name: product.name }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	openDetailPage = id => {
		this.rootStore.routerStore.goTo('master.app.main.product.list.detail', {
			id,
		});
	};

	@action.bound
	linkProduct = async product => {
		this.suspend();
		try {
			const response = await this.listStore.linkProduct(product, sourceOfChange.linkProduct);
			if (response.data != 0) {
				this.rootStore.notificationStore.success(['PRODUCTVIEW.NOTIFICATIONS.LINKED', { count: response.data }]);
				this.rootStore.viewStore.countUnlinkedOrderLines();
			} else {
				this.rootStore.notificationStore.error('PRODUCTVIEW.NOTIFICATIONS.UNABLETOLINK');
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	setAsChanged = () => {
		this.change = !this.change;
	};

	@action.bound
	setButtons = () => {
		const buttons = {
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-cog spc--right--sml',
					onClick: this.baasicTableSettingsViewStore.openTableSettingsModal,
					text: 'SETTINGS',
				},
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-trash spc--right--sml',
					onClick: () => {
						this.removeFiltersModal.handleRemoveFiltersButtonClick();
					},
					text: 'FILTER.REMOVEALL',
					disabled: !!(this.queryUtility.filter.advancedSearch === undefined),
				},
			],
		};
		if (!isWarehouse(this.rootStore)) {
			buttons.buttons.unshift({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-file-import spc--right--sml',
				onClick: () => this.openImportProductsModal(true),
				text: 'PRODUCTVIEW.IMPORTVIRTUALSKUS',
			});
			buttons.buttons.unshift({
				className: 'btn btn--dropdown header__button',
				iconClassName: 'fa fa-file-import spc--right--sml',
				onClick: () => this.openImportProductsModal(false),
				text: 'PRODUCTVIEW.IMPORTPRODUCTS',
			});
		}
		buttons.buttons.unshift({
			className: 'btn btn--dropdown header__button',
			iconClassName: 'fa fa-plus spc--right--sml',
			onClick: this.routes.create,
			authorization: 'productSection.create',
			text: 'PRODUCTVIEW.NEW',
		});

		this.setButtonComponent(buttons);
	};

	@action.bound closeProductImportModal() {
		this.sellerDropdownStore.clear();
		this.rootStore.routerStore.updateQueryParams({
			importData: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	@action.bound downloadExcelTemplate() {
		const workbook = new excelJS.Workbook();
		const requiredFieldStyle = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFC7CE' },
		};
		const borderStyle = {
			top: { style: 'medium', color: { argb: '27788a' } },
			left: { style: 'medium', color: { argb: '27788a' } },
			bottom: { style: 'medium', color: { argb: '27788a' } },
			right: { style: 'medium', color: { argb: '27788a' } },
		};

		workbook.created = new Date();
		workbook.modified = new Date();

		let sheet = workbook.addWorksheet();
		let templateName = '';

		if (!this.isVirtualSkus) {
			templateName = 'Products';
			sheet.columns = [
				{ header: 'IsMultiProduct', key: 'IsMultiProduct' },
				{ header: 'IsActive', key: 'IsActive' },
				{ header: 'ProductSKU', key: 'ProductSKU' },
				{ header: 'Name', key: 'Name' },
				{ header: 'Upc1', key: 'Upc1' },
				{ header: 'Upc2', key: 'Upc2' },
				{ header: 'Upc3', key: 'Upc3' },
				{ header: 'Upc4', key: 'Upc4' },
				{ header: 'Description', key: 'Description' },
				{ header: 'Condition', key: 'Condition' },
				{ header: 'ProductStatus', key: 'ProductStatus' },
				{ header: 'ProductCategory', key: 'ProductCategory' },
				{ header: 'OnePerBox', key: 'OnePerBox' },
				{ header: 'DimensionUnit', key: 'DimensionUnit' },
				{ header: 'Length', key: 'Length' },
				{ header: 'Width', key: 'Width' },
				{ header: 'Height', key: 'Height' },
				{ header: 'WeightUnit', key: 'WeightUnit' },
				{ header: 'Kilograms', key: 'Kilograms' },
				{ header: 'Pounds', key: 'Pounds' },
				{ header: 'Ounces', key: 'Ounces' },
				{ header: 'ASIN', key: 'asin' },
				{ header: 'IsShippable', key: 'IsShippable' },
				{ header: 'NmfcCode', key: 'nmfcCode' },
				{ header: 'FreightClassCode', key: 'FreightClassCode' },
			];

			_.forEach(
				[
					'A1',
					'B1',
					'C1',
					'D1',
					'E1',
					'F1',
					'G1',
					'H1',
					'I1',
					'J1',
					'K1',
					'L1',
					'M1',
					'N1',
					'O1',
					'P1',
					'Q1',
					'R1',
					'S1',
					'T1',
					'U1',
					'V1',
					'W1',
					'X1',
					'Y1',
				],
				cell => {
					sheet.getCell(cell).border = borderStyle;
				}
			);

			sheet.getCell('B1').fill = requiredFieldStyle;
			sheet.getCell('C1').fill = requiredFieldStyle;
			sheet.getCell('D1').fill = requiredFieldStyle;
			sheet.getCell('K1').fill = requiredFieldStyle;

			sheet.getCell('A1').note = 'Valid entry: 0,1,true,false';
			sheet.getCell('B1').note = 'Valid entry: 0,1,true,false';
			sheet.getCell('E1').note = 'Required for shippable products';
			sheet.getCell('M1').note = 'Valid entry: 0,1,true,false';
			sheet.getCell('O1').note =
				'Numbers have to be greather than 0, 2 decimal digits maximum. Required for shippable products.';
			sheet.getCell('P1').note =
				'Numbers have to be greather than 0, 2 decimal digits maximum. Required for shippable products.';
			sheet.getCell('Q1').note =
				'Numbers have to be greather than 0, 2 decimal digits maximum. Required for shippable products.';
			sheet.getCell('N1').note = 'Inches/Meters. Required for shippable products.';
			sheet.getCell('J1').note = 'Required for shippable products. Must enter value that exists in the system.';
			sheet.getCell('K1').note = 'Must enter value that exists in the system';
			sheet.getCell('L1').note = 'Must enter value that exists in the system';
			sheet.getCell('R1').note = 'Kilograms/Pounds and Ounces Required for shippable products.';
			sheet.getCell('S1').note = 'Required if Kilograms are chosen. Required for shippable products.';
			sheet.getCell('T1').note = 'Required if Pounds and Ounces are chosen. Required for shippable products.';
			sheet.getCell('W1').note = 'Valid entry: 0,1,true,false. Default value: true';
			sheet.getCell('X1').note = 'Numbers have to be greather than 0, 2 decimal digits maximum.';
			sheet.getCell('Y1').note = 'Numbers have to be greather than 0, 1 decimal digit maximum.';
		} else {
			templateName = 'VirtualSkus';
			sheet.columns = [
				{ header: 'SKU', key: 'SKU' },
				{ header: 'VirtualSKU', key: 'VirtualSKU' },
				{ header: 'ChannelName', key: 'ChannelNames' },
				{ header: 'Carriers', key: 'Carriers' },
				{ header: 'PostInventory', key: 'PostInventory' },
			];

			sheet.getCell('A1').fill = requiredFieldStyle;
			sheet.getCell('B1').fill = requiredFieldStyle;
			sheet.getCell('C1').fill = requiredFieldStyle;

			sheet.getCell('A1').border = borderStyle;
			sheet.getCell('B1').border = borderStyle;
			sheet.getCell('C1').border = borderStyle;
			sheet.getCell('D1').border = borderStyle;
			sheet.getCell('E1').border = borderStyle;

			sheet.getCell('A1').note =
				'Must enter value that exists in the system. If SKU is not active virtual sku and post inventory will be set to false by default.';
			sheet.getCell('C1').note = 'Must enter value that exists in the system';
			sheet.getCell('D1').note = 'Must enter value that exists in the system';
			sheet.getCell('E1').note = 'Valid entry: 0,1,true,false';
		}
		sheet.name = `Import${templateName}Template`;

		workbook.xlsx.writeBuffer().then(function(buffer) {
			const blob = new Blob([buffer], { type: 'application/xlsx' });
			saveAs(blob, `Import${templateName}Template.xlsx`);
		});
	}

	@action.bound async getLookups(keys) {
		await Promise.all(
			keys.map(async key => {
				await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName[key]);
				this[`${key}Options`] = _.map(this.lookupStore.data, item => {
					return { label: item.name, value: item.id, extra: item };
				});
			})
		);
	}

	validateColumns = item => {
		return (
			item.hasOwnProperty('IsActive') &&
			item.hasOwnProperty('ProductSKU') &&
			item.hasOwnProperty('Name') &&
			item.hasOwnProperty('ProductStatus')
		);
	};

	validateBoolField = item => {
		const validItems = [0, 1, true, false, 'true', 'false', '0', '1'];
		return validItems.includes(item);
	};

	@action.bound
	createProducts = async items => {
		try {
			if (items.length > 0) {
				let emptyField = '';
				let decimalLength = '';
				let minValue = '';
				let missingRequiredField = false;
				let productsToCreate = [];
				for (let item of items) {
					if (this.validateColumns(item)) {
						if (item.Name == undefined) {
							emptyField = 'Name';
							break;
						} else {
							item.Name = String(item.Name).trim();
						}
						if (item.ASIN) {
							item.ASIN = String(item.ASIN).trim();
						}
						if (item.Description) {
							item.Description = String(item.Description).trim();
						}
						if (item.ProductSKU == undefined) {
							emptyField = 'ProductSKU';
							break;
						} else {
							item.mainSku = String(item.ProductSKU).trim();
							delete item.ProductSKU;
						}

						item.SellerId =
							this.productImportForm.$('sellerId').value || this.rootStore.applicationStore.currentOrganization.id;

						if (item.IsActive == undefined || !this.validateBoolField(item.IsActive)) {
							emptyField = 'isActive';
							break;
						}

						if (item.IsMultiProduct != undefined && !this.validateBoolField(item.IsMultiProduct)) {
							emptyField = 'IsMultiProduct';
							break;
						}

						if (item.OnePerBox != undefined && !this.validateBoolField(item.OnePerBox)) {
							emptyField = 'OnePerBox';
							break;
						}

						if (item.IsShippable == undefined) item.IsShippable = true;

						if (!this.validateBoolField(item.IsShippable)) {
							emptyField = 'IsShippable';
							break;
						} else {
							if (item.IsShippable) {
								if (item.Upc1 == undefined) {
									emptyField = 'Upc1';
									break;
								} else {
									let upcs = _.map([item.Upc1, item.Upc2, item.Upc3, item.Upc4], upcItem => {
										return { upc: upcItem && upcItem.toString().trim() !== '' ? upcItem.toString().trim() : null };
									});
									item.ProductUpcs = _.filter(upcs, item => {
										return item.upc != null;
									});
									delete item.Upc1;
									delete item.Upc2;
									delete item.Upc3;
									delete item.Upc4;
								}

								if (item.Height == undefined || !validateNumberField(item.Height)) {
									emptyField = 'Height';
									break;
								} else if (!validateMinValue(item.Height)) {
									minValue = 'Height';
									break;
								} else if (!validateDecimalLengthField(item.Height)) {
									decimalLength = 'Height';
									break;
								}

								if (item.Length == undefined || !validateNumberField(item.Length)) {
									emptyField = 'Length';
									break;
								} else if (!validateMinValue(item.Length)) {
									minValue = 'Length';
									break;
								} else if (!validateDecimalLengthField(item.Length)) {
									decimalLength = 'Length';
									break;
								}

								if (item.Width == undefined || !validateNumberField(item.Width)) {
									emptyField = 'Width';
									break;
								} else if (!validateMinValue(item.Width)) {
									minValue = 'Width';
									break;
								} else if (!validateDecimalLengthField(item.Width)) {
									decimalLength = 'Width';
									break;
								}
								emptyField = this.validateUnitsOnImport(item);
								if (emptyField) break;
							}
						}
						if (!validateDecimalLengthField(item.NmfcCode)) {
							decimalLength = 'NmfcCode';
							break;
						}
						if (!validateDecimalLengthField(item.FreightClassCode, 1)) {
							decimalLength = 'FreightClassCode';
							break;
						}
						var productStatus = _.find(this.productStatusOptions, productStatus => {
							if (productStatus.label == String(item.ProductStatus).trim()) {
								return productStatus.value;
							}
						});
						if (productStatus !== undefined) {
							item.ProductStatusId = productStatus.value;
						} else {
							emptyField = 'ProductStatus';
							break;
						}
						delete item.ProductStatus;

						if (item.ProductCategory != null && item.ProductCategory != undefined) {
							var productCategory = _.find(this.productCategoryOptions, productCategory => {
								if (productCategory.label == String(item.ProductCategory).trim()) {
									return productCategory.value;
								}
							});
							if (productCategory !== undefined) {
								item.ProductCategoryId = productCategory.value;
							} else {
								emptyField = 'ProductCategory';
								break;
							}
							delete item.ProductCategory;
						}

						var box = _.find(this.boxOptions, box => {
							if (box.label == item.Box) {
								return box.value;
							}
						});
						if (box !== undefined) {
							item.BoxId = box.value;
						}
						delete item.Box;
					} else {
						missingRequiredField = true;
						break;
					}
					productsToCreate.push(
						item.IsShippable
							? item
							: {
									isActive: item.IsActive,
									mainSku: item.mainSku,
									name: item.Name,
									productStatusId: item.ProductStatusId,
									isShippable: item.IsShippable,
									sellerId: item.SellerId,
									asin: item.ASIN,
									productCategoryId: item.ProductCategoryId,
									nmfcCode: item.NmfcCode,
									freightClassCode: item.FreightClassCode,
									productUpcs: [],
							  }
					);
				}

				if (emptyField !== '') {
					this.loading = false;
					this.rootStore.notificationStore.error(['PRODUCTVIEW.EMPTYFIELD', { field: emptyField }]);
				} else if (missingRequiredField) {
					this.loading = false;
					this.rootStore.notificationStore.error(['PRODUCTVIEW.REQUIREDCOLUMNMISSING']);
				} else if (minValue !== '') {
					this.loading = false;
					this.rootStore.notificationStore.error(['PRODUCTVIEW.MINVALUE', { field: minValue }]);
				} else if (decimalLength !== '') {
					this.loading = false;
					let length = 2;
					if (decimalLength === 'FreightClassCode') {
						length = 1;
					}
					this.rootStore.notificationStore.error([
						'PRODUCTVIEW.DECIMALLENGTH',
						{ field: decimalLength, length: `${length}` },
					]);
				} else {
					await this.listStore.createProducts(productsToCreate, sourceOfChange.createProductFromXLS);
					this.rootStore.notificationStore.success(['PRODUCTVIEW.PRODUCTIMPORT']);
					await this.findProduct();
				}
			} else {
				this.loading = false;
				this.rootStore.notificationStore.error(['PRODUCTVIEW.REQUIREDCOLUMNMISSING']);
			}
		} catch ({ statusCode, data }) {
			this.loading = false;
			if (data && data.message) {
				if (statusCode === 409) {
					this.rootStore.notificationStore.warning(['PRODUCTVIEW.CONFLICT']);
				} else {
					this.rootStore.notificationStore.warning(data.message);
				}
			} else {
				this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERROR']);
			}
		}
	};

	@action.bound
	validateUnitsOnImport = item => {
		let condition = _.find(this.conditionOptions, condition => {
			if (condition.label == String(item.Condition).trim()) {
				return condition.value;
			}
		});
		if (condition) {
			item.ConditionId = condition.value;
		} else {
			return 'Condition';
		}
		delete item.Condition;

		var dimensionUnit = _.find(this.dimensionUnitOptions, dimensionUnit => {
			if (dimensionUnit.label == String(item.DimensionUnit).trim()) {
				return dimensionUnit.value;
			}
		});
		if (dimensionUnit !== undefined) {
			item.DimensionUnitId = dimensionUnit.value;
		} else {
			return 'DimensionUnit';
		}
		delete item.DimensionUnit;

		var weightUnit = _.find(this.weightUnitOptions, weightUnit => {
			if (weightUnit.label == String(item.WeightUnit).trim()) {
				if (weightUnit.extra.abrv == 'pounds%20and%20ounces') {
					if (item.Pounds !== undefined) {
						const decimalPounds = getDecimalPounds(item.Pounds, item.Ounces);
						item.Weight = decimalPounds == '' ? 0 : decimalPounds;
					} else {
						return 'Pounds';
					}
				} else if (weightUnit.extra.abrv == 'kilograms') {
					if (item.Kilograms !== undefined && !isNaN(item.Kilograms)) {
						item.Weight = parseFloat(item.Kilograms).toFixed(2);
					} else {
						return 'Kilograms';
					}
				} else {
					item.Weight = 0;
				}

				return weightUnit.value;
			}
		});
		if (weightUnit !== undefined) {
			item.WeightUnitId = weightUnit.value;
		} else {
			return 'WeightUnit';
		}
		delete item.WeightUnit;

		return '';
	};

	@action.bound async onFilesDrop(filesMeta) {
		this.productImportForm.validate().then(({ isValid }) => {
			if (isValid) {
				if (filesMeta && filesMeta.length == 1) {
					let fileMeta = filesMeta[0];
					let reader = new FileReader();
					let xlsx = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
					let xls = 'application/vnd.ms-excel';
					if (
						fileMeta.type === xlsx ||
						(fileMeta.type === xls && !fileMeta.name.includes('csv', fileMeta.name.length - 3))
					) {
						// Excel processing (excluding csv)
						reader.onload = async evt => {
							const workbook = XLSX.read(evt.target.result, { type: 'binary' });
							const firstWorksheet = workbook.Sheets[workbook.SheetNames[0]];
							let data = XLSX.utils.sheet_to_json(firstWorksheet);
							if (data.length > 0) {
								if (!this.isVirtualSkus) {
									this.loading = true;
									await this.getLookups([
										'condition',
										'productStatus',
										'productCategory',
										'weightUnit',
										'dimensionUnit',
										'box',
									]);
									this.createProducts(data);
								} else {
									this.channelStore = new ChannelStore(this.rootStore);
									this.carrierStore = new CarrierStore(this.rootStore);
									this.createVirtualSKUs(data);
								}
							} else {
								this.loading = false;
								this.rootStore.notificationStore.error(['PRODUCTVIEW.NOVALIDDATAFORIMPORTFOUND']);
							}
						};
						reader.readAsBinaryString(fileMeta);
					} else {
						this.loading = false;
						this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERBADFORMAT']);
					}
				} else if (filesMeta && filesMeta.length == 0) {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERROR']);
				} else {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.PRODUCTIMPORTERROR']);
				}
			} else {
				this.productImportForm.showErrors();
			}
		});
	}

	@action.bound
	findProduct = async () => {
		this.suspend();
		if (this.baasicTableSettingsViewStore == undefined) {
			this.baasicTableSettingsViewStore = new BaasicTableSettingsViewStore(this.rootStore);
		}
		await this.baasicTableSettingsViewStore.setTemplates(
			this.tableData,
			'ProductsTableSettings',
			this.setAsChanged,
			this.findProduct
		);
		this.queryUtility.setFilterParams(
			this,
			{
				isDeleted: true,
				isActive: this.showOnlyActiveSkus ? true : null,
				productInventoryAssignedWarehouseId:
					this.rootStore.applicationStore.organizationType == 'warehouse'
						? this.rootStore.applicationStore.currentOrganization.id
						: null,
			},
			this.tableFilter
		);
		await this.listStore.findProduct(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.queryUtility.setFilterPersistance(this, 'product-filter', 'product-table-data');

		this.setButtons();
		this.resume();
	};

	@action.bound
	deleteProduct = async item => {
		this.suspend();
		try {
			await this.listStore.deleteProduct(item);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.PRODUCT', { name: item.name }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	onCheck = check => {
		if (check) {
			this.showOnlyActiveSkus = check.currentTarget.checked;
			this.findProduct();
		}
	};

	getLinkProductConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.linkProduct(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['PRODUCTVIEW.LINKPRODUCTCONFIRM', { name: item.name }],
			data: item,
		};
	};

	getProductActivityConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.isActiveToggle(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['PRODUCTVIEW.SETPRODUCTACTIVITY', { name: item.name }],
			data: item,
		};
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteProduct(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['LOOKUPDELETECONFIRM.PRODUCT', { name: item.name }],
			data: item,
		};
	};

	@action.bound
	createVirtualSKUs = async items => {
		const notificationId = this.rootStore.notificationStore.load(translate('BULKNOTIFICATION.IMPORTVIRTUALSKUS'));
		try {
			if (items.length > 0) {
				let emptyField = '';
				let missingRequiredField = false;
				let mainSkus = [];
				const sellerIdFilter =
					this.productImportForm.$('sellerId').value || this.rootStore.applicationStore.currentOrganization.id;
				await this.channelStore.findChannel({
					page: 1,
					rpp: 200,
					sellerIds: [sellerIdFilter],
					embed: [],
				});

				await this.carrierStore.find({
					page: 1,
					rpp: 200,
					sellerIds: [sellerIdFilter],
					embed: [],
				});

				for (let item of items) {
					if (item.hasOwnProperty('SKU') && item.hasOwnProperty('VirtualSKU') && item.hasOwnProperty('ChannelName')) {
						if (item.SKU == undefined) {
							emptyField = 'SKU';
							break;
						}

						if (item.VirtualSKU == undefined) {
							emptyField = 'VirtualSKU';
							break;
						}

						var channel = _.find(this.channelStore.data, channel => {
							if (channel.name == item.ChannelName) {
								return channel;
							}
						});

						if (channel == undefined) {
							emptyField = 'ChannelName';
							break;
						}

						if (item.PostInventory != undefined && !this.validateBoolField(item.PostInventory)) {
							emptyField = 'PostInventory';
							break;
						}

						if (item.Carriers != null && item.Carriers != undefined) {
							item.Carriers = item.Carriers.split(',').map(p => p.trim());

							let carriers = _.map(this.carrierStore.data.filter(i => item.Carriers.includes(i.name)), carrier => {
								return {
									value: carrier.id,
									label: carrier.name,
									extra: carrier,
								};
							});

							if (carriers.length > 0) {
								item.Carriers = carriers;
							} else {
								emptyField = 'Carriers';
								break;
							}
						}

						let mainSku = item.SKU;
						if (!mainSkus.hasOwnProperty(mainSku)) {
							mainSkus[mainSku] = [];
						}
						mainSkus[mainSku].push({
							sku: String(item.VirtualSKU).trim(),
							channelId: channel.id,
							channel: null,
							isActive: true,
							description: '',
							id: '',
							postInventory: this.translateToBoolean(item.PostInventory),
							searchId: mainSkus[mainSku].length,
							productVirtualSkuCarriers: _.map(item.Carriers, carrier => {
								return {
									productVirtualSkuId: '',
									carrierId: carrier.value,
									id: '',
								};
							}),
						});
					} else {
						missingRequiredField = true;
						break;
					}
				}

				if (emptyField !== '') {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.EMPTYFIELD', { field: emptyField }]);
				} else if (missingRequiredField) {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.REQUIREDCOLUMNMISSING']);
				} else {
					const mainSkuKeys = Object.keys(mainSkus);
					await this.productStore.findProduct({
						mainSkus: mainSkuKeys,
						pageSize: mainSkuKeys.length,
						isDeleted: true,
						embed: this.queryUtility.filter.embed,
						sellerIds: [sellerIdFilter],
					});
					let products = this.productStore.data;
					if (products && products.length == mainSkuKeys.length) {
						for (let product of products) {
							if (product.productVirtualSkus.length == 0) {
								product.productVirtualSkus = mainSkus[product.mainSku];
							} else {
								const importedVirtualSkus = mainSkus[product.mainSku];

								for (let importedVirtualSku of importedVirtualSkus) {
									const existingVirtualSku = product.productVirtualSkus.find(
										virtualSku =>
											virtualSku.sku === importedVirtualSku.sku && virtualSku.channelId === importedVirtualSku.channelId
									);

									if (existingVirtualSku) {
										const index = product.productVirtualSkus.findIndex(
											vsku => vsku.sku === existingVirtualSku.sku && vsku.channelId === existingVirtualSku.channelId
										);

										if (index !== -1) {
											product.productVirtualSkus[index].isActive = importedVirtualSku.isActive;
											product.productVirtualSkus[index].postInventory = importedVirtualSku.postInventory;
											product.productVirtualSkus[index].productVirtualSkuCarriers =
												importedVirtualSku.productVirtualSkuCarriers;
										}
									} else {
										product.productVirtualSkus.push(importedVirtualSku);
									}
								}
							}
							if (!product.isActive) {
								for (let productVirtualSku of product.productVirtualSkus) {
									productVirtualSku.isActive = false;
									productVirtualSku.postInventory = false;
								}
							}
							if (!product.isShippable) {
								for (let productVirtualSku of product.productVirtualSkus) {
									productVirtualSku.postInventory = false;
									productVirtualSku.productVirtualSkuCarriers = [];
								}
							}
						}

						await this.productStore.updateProducts(products, sourceOfChange.createVirtualSKUFromXLS);
						this.rootStore.notificationStore.success(['PRODUCTVIEW.VIRTUALSKUIMPORT']);
					} else {
						for (let sku of mainSkuKeys) {
							if (
								!_.some(products, item => {
									return item.mainSku === sku;
								})
							) {
								this.rootStore.notificationStore.error(['PRODUCTVIEW.SKUNOTFOUNDERROR', { sku: sku }]);
								break;
							}
						}
					}
				}
			} else {
				this.rootStore.notificationStore.error(['PRODUCTVIEW.REQUIREDCOLUMNMISSING']);
			}
		} catch ({ statusCode, data }) {
			if (data && data.message) {
				if (statusCode === 409) {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.VIRTUALSKUCONFLICT']);
				} else if (statusCode === 404) {
					this.rootStore.notificationStore.error(['PRODUCTVIEW.SKUNOTFOUNDERROR', { sku: data.message }]);
				} else {
					this.rootStore.notificationStore.error(data.message);
				}
			} else {
				this.rootStore.notificationStore.error(['PRODUCTVIEW.VIRTUALSKUERROR']);
			}
		}
		this.rootStore.notificationStore.dismiss(notificationId);
	};

	translateToBoolean = value => {
		if (value === 1 || value === 'true') return true;
		else if (value === 0 || value === 'false') return false;
	};
}

export default ProductListViewStore;



// WEBPACK FOOTER //
// ./src/modules/product/stores/ProductListViewStore.js